import { fromEvent as observableFromEvent, Observable, Subscription } from 'rxjs';
import { map, share } from 'rxjs/operators';
import * as socketIo from 'socket.io-client';
import { SocketEvents } from '@core/services/offer/_private/models/enums';
import OfferRequest from '@core/services/offer/_private/models/OfferRequest';
import { OfferRequestParams } from '@core/services/offer/_private/models/OfferRequestParams';
import ErrorType from '@errors/network/ErrorType';
import { RequestError } from '@errors/network/RequestError';
import MarketGroupInfo from '@models/struct/MarketGroupInfo';
import Logger from '@loggers/Logger';
import {
    SOCKET_CONNECTION_ERROR_LOGGER_THROTTLE
} from '@core/constants';
import BufferedSocket from '@core/services/common/BufferedSocket';
import MarketDto from '@core/mappers/struct/dtos/MarketDto';
import MarketGroupDto from '@core/mappers/struct/dtos/MarketGroupDto';
import Market, { createMarket } from '@models/struct/Market';
import OddTypeDto from '@core/mappers/struct/dtos/OddTypeDto';
import ConnectOpts = SocketIOClient.ConnectOpts;
import Sport from '@models/struct/Sport';
import Category from '@models/struct/Category';
import Tournament from '@models/struct/Tournament';
import OddType from '@models/struct/OddType';
import MarketGroupMapper from '@core/mappers/struct/MarketGroupMapper';
import SportMapper from '@core/mappers/struct/SportMapper';
import TournamentMapper from '@core/mappers/struct/TournamentMapper';
import CategoryMapper from '@core/mappers/struct/CategoryMapper';
import OddTypeMapper from '@core/mappers/struct/OddTypeMapper';

class SocketConfig {
    public connectErrorLoggerThrottle = SOCKET_CONNECTION_ERROR_LOGGER_THROTTLE;
}
const sportMapper = new SportMapper();
const tournamentMapper = new TournamentMapper();
const categoryMapper = new CategoryMapper();
const oddTypeMapper = new OddTypeMapper();
const marketGroupMapper = new MarketGroupMapper();

export default class Socket extends BufferedSocket<OfferRequest> {
    public logger = Logger;
    private socket: SocketIOClient.Socket;
    private url: string;
    private connectOptions: any;
    private socketConfig: SocketConfig;
    private data$: Observable<any>;
    private allEventChanges$: Observable<any>;
    private structChange$: Observable<any>;
    private socketConnected$: Observable<any>;
    private socketReconnected$: Observable<any>;
    private socketConnectError$: Observable<any>;
    private socketError$: Observable<any>;
    private socketDisconnected$: Observable<any>;
    private liveEventsCountChanges$: Observable<any>;
    private marketGroup$: Observable<MarketGroupInfo>;
    private sport$: Observable<any>;
    private category$: Observable<any>;
    private tournament$: Observable<any>;
    private market$: Observable<any>;
    private oddType$: Observable<any>;

    // this are just for internal subscribers for some of the socket basic events
    private socketBasicEventsSubscriptions: Subscription[] = [];

    constructor(
        url: string,
        connectOptions: ConnectOpts = {
            secure: false,
            transports: ['websocket'],
            upgrade: false,
        },
        socketConfig: SocketConfig = new SocketConfig()
    ) {
        super();
        this.url = url;
        this.connectOptions = connectOptions;
        this.socketConfig = socketConfig;
    }

    /**
     * @throws {RequestError}
     * @throws {Error}
     */
    public start(): void {
        try {
            this.socket = socketIo(this.url, this.connectOptions);
            this.emitSubscriptionToAllChanges();
            this.emitSubscriptionToLiveEventsCountChanges();
            this.emitSubscriptionToStructDiffs();
            this.emitSubscriptionToOutrights();
            this.createStreams();
            this.subscribeToSocketBasicEvents();
        } catch (e) {
            this.logger.error(e);

            // if we cant initialize we want the whole thing down
            this.stop();
            throw new RequestError(e, ErrorType.socketConnectionError);
        }
    }

    /**
     * @throws {RequestError}
     * @throws {Error}
     */
    public stop(): void {
        if (this.socket) {
            this.emitRemoveSubscriptionToAllChanges();
            this.emitSubscriptionToStructDiffs();
            this.emitRemoveSubscriptionToOutrights();
            this.emitRemoveSubscriptionToLiveEventsCountChanges();
            this.emitRemoveSubscriptionToStructDiffs();
            this.unsubscribeFromSocketBasicEvents();
            this.waitingRequestRegister.clear();
            this.socketBasicEventsSubscriptions = [];

            try {
                this.socket.disconnect();
            } catch (e) {
                this.logger.error(e);
                throw new RequestError(e, ErrorType.socketDisconnectError);
            }
        }
    }

    public getAllEventChanges$(): Observable<any> {
        return this.allEventChanges$;
    }

    public getData$(): Observable<any> {
        return this.data$;
    }

    public getSocketReconnected$(): Observable<any> {
        return this.socketReconnected$;
    }
    public getLiveEventCountChanges$(): Observable<any> {
        console.log('EVENT LIVE CHANGES');
        return this.liveEventsCountChanges$;
    }
    public emitSubscriptionToEvent(eventId: number): void {
        this.emit(SocketEvents.addSubscription, OfferRequest.createEventSubscription(eventId));
    }
    public emitSubscriptionToStructDiffs(): void {
        this.emit(SocketEvents.addSubscription, {
            structDiff: true,
        });
    }
    public emitSubscriptionToOutrights(): void {
        this.emit(SocketEvents.addSubscription, {
            outrights: true,
        });
    }
    public emitRemoveSubscriptionToOutrights(): void {
        this.emit(SocketEvents.removeSubscription, {
            outrights: true,
        });
    }
    public emitRemoveSubscriptionToStructDiffs(): void {
        this.emit(SocketEvents.removeSubscription, {
            structDiff: true,
        });
    }
    public emitRemoveSubscriptionForEvent(eventId: number): void {
        this.emit(SocketEvents.removeSubscription, OfferRequest.createEventSubscription(eventId));
    }
    public getSocketConnected$(): Observable<any> {
        return this.socketConnected$;
    }

    public getSocketConnectError$(): Observable<any> {
        return this.socketConnectError$;
    }

    public getSocketDisconnected$(): Observable<any> {
        return this.socketDisconnected$;
    }

    public getSocketError$(): Observable<any> {
        return this.socketError$;
    }

    public getStructChange$(): Observable<any> {
        return this.structChange$;
    }

    // todo test
    public emit = (event: string, offerRequest: OfferRequestParams): void => {
        if (this.isConnected) {
            this.rawEmit(event, JSON.stringify(offerRequest));
        } else {
            // @ts-ignore
            this.waitingRequestRegister.add(event, offerRequest);
        }
    }

    protected onSocketReconnect = () => {
        this.isConnected = true;
        this.emitSubscriptionToAllChanges();
        this.emitSubscriptionToLiveEventsCountChanges();
        this.emitSubscriptionToStructDiffs();
    };

    private emitSubscriptionToAllChanges(): void {
        this.emit(SocketEvents.addSubscription, {
            all: true,
            preselected: true,
        });
    }

    private emitRemoveSubscriptionToAllChanges(): void {
        this.emit(SocketEvents.removeSubscription, {
            all: true,
            preselected: true,
        });
    }

    private emitSubscriptionToLiveEventsCountChanges(): void {
        this.emit(SocketEvents.addSubscription, {
            liveEventsNum: true,
        });
    }

    private emitRemoveSubscriptionToLiveEventsCountChanges(): void {
        this.emit(SocketEvents.addSubscription, OfferRequest.createLiveEventsCountChangesRequest(false));
    }

    private subscribeToStream(stream: Observable<any>, callback: any) {
        this.socketBasicEventsSubscriptions.push(stream.subscribe(callback));
    }

    private createStreams() {
        this.socketConnected$ = this.toObservable(SocketEvents.Connect);
        this.socketReconnected$ = this.toObservable(SocketEvents.Reconnect);
        this.socketDisconnected$ = this.toObservable(SocketEvents.Disconnect);
        this.data$ = this.toObservable(SocketEvents.getDataCompressed);
        this.allEventChanges$ = this.toObservable(SocketEvents.eventChange);
        this.liveEventsCountChanges$ = this.toObservable(SocketEvents.liveCountChange);

        this.sport$ = this.toObservable<[string, string]>(SocketEvents.Sport)
            .pipe(map(dto => sportMapper.createTargetObject(dto[1], dto[0])));

        this.category$ = this.toObservable<[string, string]>(SocketEvents.Category)
            .pipe(map(dto => categoryMapper.createTargetObject(dto[1], dto[0])));

        this.tournament$ = this.toObservable<[string, string]>(SocketEvents.Tournament)
            .pipe(map(dto => tournamentMapper.createTargetObject(dto[1], dto[0])));

        this.market$ = this.toObservable<[string, MarketDto]>(SocketEvents.BetGroup)
            .pipe(map(createMarket));

        this.oddType$ = this.toObservable<[string, OddTypeDto]>(SocketEvents.OddType)
            .pipe(map(dto => oddTypeMapper.createTargetObject(dto[1], dto[0])));

        this.marketGroup$ = this.toObservable<[string, MarketGroupDto]>(SocketEvents.MarketGroup)
            .pipe(map(t => marketGroupMapper.map(t[1])));
    }
    private toObservable<T>(event: string): Observable<T> {
        return observableFromEvent<T>(this.socket, event).pipe(share());
    }

    private subscribeToSocketBasicEvents() {
        this.subscribeToStream(this.socketConnected$, this.onSocketConnect);
        this.subscribeToStream(this.socketReconnected$, this.onSocketReconnect);
        this.subscribeToStream(this.socketDisconnected$, this.onSocketDisconnect);
    }

    private unsubscribeFromSocketBasicEvents() {
        this.socketBasicEventsSubscriptions.forEach((subscription) => subscription.unsubscribe());
    }

    rawEmit = (event: string, ...args: any[]) => {
        this.socket.emit(event, ...args);
    }
    public getSport$(): Observable<Sport> {
        return this.sport$;
    }

    public getCategory$(): Observable<Category> {
        return this.category$;
    }

    public getTournament$(): Observable<Tournament> {
        return this.tournament$;
    }

    public getMarket$(): Observable<Market> {
        return this.market$;
    }

    public getOddType$(): Observable<OddType> {
        return this.oddType$;
    }

    public getMarketGroup$(): Observable<MarketGroupInfo> {
        return this.marketGroup$;
    }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--12!./src/core/services/offer/_private/Socket.ts